import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import CajaDatosService from '../../axios_services/cajaflujodatos.service';
import Loader from '../Loader/Loader';

const ModalCajaFinancieroPresupuesto = ({
    presupuesto,
    onSave,
    onClose,
    isOpen,
    perfilId,
    usuarioId
}) => {
    const [loading, setLoading] = useState(false);
    const [categorias, setCategorias] = useState([]);
    const [conceptos, setConceptos] = useState([]);
    const [loadingData, setLoadingData] = useState(false);

    const getInitialState = () => ({
        periodo_fecha: '',
        categoria_id: '',
        concepto_id: '',
        monto: ''
    });

    const [formData, setFormData] = useState(getInitialState());

    const conceptosFiltrados = React.useMemo(() => {
        if (!formData.categoria_id) {
            return conceptos;
        }
        return conceptos.filter(con => con.caja_categoria_id?.toString() === formData.categoria_id);
    }, [formData.categoria_id, conceptos]);

    useEffect(() => {
        const loadData = async () => {
            if (isOpen && perfilId) {
                setLoadingData(true);
                try {
                    const [categoriasRes, conceptosRes] = await Promise.all([
                        CajaDatosService.getCajaCategoria(perfilId),
                        CajaDatosService.getCajaConcepto(perfilId)
                    ]);

                    if (categoriasRes.success) {
                        setCategorias(categoriasRes.data || []);
                    }

                    if (conceptosRes.success) {
                        setConceptos(conceptosRes.data || []);
                    }
                } catch (error) {
                    console.error('Error al cargar datos:', error);
                    alert('Error al cargar las categorías y conceptos');
                } finally {
                    setLoadingData(false);
                }
            }
        };

        loadData();
    }, [isOpen, perfilId]);

    useEffect(() => {
        if (isOpen) {
            if (presupuesto) {
                setFormData({
                    periodo_fecha: presupuesto.periodo_fecha || '',
                    categoria_id: presupuesto.caja_categoria_id?.toString() || '',
                    concepto_id: presupuesto.caja_concepto_id?.toString() || '',
                    monto: presupuesto.monto?.toString() || ''
                });
            } else {
                const now = new Date();
                const periodoActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                setFormData({
                    ...getInitialState(),
                    periodo_fecha: periodoActual
                });
            }
        }
    }, [presupuesto, isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevData) => {
            const newData = {
                ...prevData,
                [name]: value,
            };

            if (name === 'categoria_id') {
                newData.concepto_id = '';
            }

            return newData;
        });
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);

            if (!formData.periodo_fecha || !formData.categoria_id || formData.monto === '') {
                alert('Por favor, complete todos los campos obligatorios.');
                setLoading(false);
                return;
            }

            if (isNaN(formData.monto)) {
                alert('Por favor, ingrese un monto válido.');
                setLoading(false);
                return;
            }

            const dataToSave = {
                periodo_fecha: formData.periodo_fecha,
                caja_categoria_id: parseInt(formData.categoria_id, 10) || null,
                caja_concepto_id: parseInt(formData.concepto_id, 10) || null,
                monto: parseFloat(formData.monto) || 0,
            };

            await onSave(dataToSave);
            onClose();

        } catch (error) {
            console.error("Error al guardar:", error);
            const errorMsg = error.response?.data?.message || 'Ocurrió un error al guardar.';
            alert(`Error: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static">
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>{presupuesto ? 'EDITAR PRESUPUESTO' : 'NUEVO PRESUPUESTO'}</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>
            <div className="modal-body-footer">
                {loading || loadingData ? (
                    <Loader />
                ) : (
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup>
                                <Label>Periodo (Mes/Año):</Label>
                                <Input
                                    className="form-control"
                                    type="month"
                                    name="periodo_fecha"
                                    value={formData.periodo_fecha}
                                    onChange={handleChange}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>Categoría:</Label>
                                <Input
                                    className="form-control"
                                    type="select"
                                    name="categoria_id"
                                    value={formData.categoria_id}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Seleccione...</option>
                                    {categorias.map((categoria) => (
                                        <option key={categoria.caja_categoria_id} value={categoria.caja_categoria_id.toString()}>
                                            {categoria.nombre_categoria}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label>Concepto:</Label>
                                <Input
                                    className="form-control"
                                    type="select"
                                    name="concepto_id"
                                    value={formData.concepto_id}
                                    onChange={handleChange}
                                >
                                    <option value="">Seleccione...</option>
                                    {conceptosFiltrados.map((concepto) => (
                                        <option key={concepto.caja_concepto_id} value={concepto.caja_concepto_id.toString()}>
                                            {concepto.nombre_concepto}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>

                            <FormGroup>
                                <Label>Monto:</Label>
                                <div className="input-group custom-input-group">
                                    <span className="input-group-text custom-symbol">S/.</span>
                                    <Input
                                        className="form-control"
                                        type="number"
                                        name="monto"
                                        value={formData.monto}
                                        onChange={handleChange}
                                        step="0.01"
                                        placeholder="0.00"
                                        required
                                    />
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <div className="button-container">
                                    <Button
                                        className="button-custom"
                                        onClick={async () => {
                                            if (window.confirm('¿Está seguro de guardar este presupuesto?')) {
                                                await handleSubmit();
                                            }
                                        }}
                                    >
                                        GUARDAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                )}
            </div>
        </Modal>
    );
};

export default ModalCajaFinancieroPresupuesto;